/**
 * Rescues links that were shared while the app used HashRouter.
 *
 * Until August 2026 every route on this site looked like
 * `mostkids.org/#/team` -- the path was carried in the URL fragment. The app
 * now uses BrowserRouter, so the same route is `mostkids.org/team`.
 *
 * Those old links are still out there (email signatures, directory listings,
 * social profiles, anything printed). Left alone they would still load the
 * site, because the server only ever sees `/` -- but React Router would read
 * the pathname as `/` and quietly render the home page instead of the page
 * the link pointed at.
 *
 * This rewrites the URL in place before React mounts, so the router sees the
 * real path. `replaceState` rather than `pushState` so the back button does
 * not bounce the visitor between the old and new URL.
 *
 * Only fragments that start with `#/` are touched -- an ordinary in-page
 * anchor like `#contact` is left exactly as it is.
 */
export default function applyLegacyHashRedirect(): void {
  const { hash, pathname, search } = window.location;

  if (!hash.startsWith('#/')) return;

  // Anything other than the root path means this is not a legacy hash link,
  // so leave it alone rather than guessing.
  if (pathname !== '/') return;

  // Drop the leading '#', then normalise a trailing slash ('#/team/' -> '/team')
  // so it matches the `exact` routes declared in App.tsx.
  let target = hash.slice(1);
  if (target.length > 1 && target.endsWith('/')) {
    target = target.slice(0, -1);
  }

  window.history.replaceState(null, '', target + search);
}
